import deepEqual from 'deep-equal';

import { getRowBusinessId } from './getRowBusinessId';
import { TableSidebarRowData } from './useTableSidebarProperties';

type TableRowItem = TableSidebarRowData['data'];

/**
 * Keep only properties, that are meaningful for comparing rows from a human perspective.
 * Removes technical properties and properties, that are expected to change between e.g. reordering or filtering of the data, even though the data itself is the same.
 */
function getDataProperties(item: TableRowItem): TableRowItem {
  const { originalRowIndex, ...rest } = item;

  return rest;
}

function findActiveSidebarRowIndex(
  activeRow: TableSidebarRowData,
  items: TableRowItem[]
): number {
  const activeBusinessId = getRowBusinessId(activeRow.data);

  if (activeBusinessId != null) {
    return items.findIndex(
      (item) => String(getRowBusinessId(item)) === String(activeBusinessId)
    );
  }

  console.warn(
    'Performance degradation: Page sidebar is using frontend fallback matching because the active table row has no actionRowId or id. Configure a non-null identifier datacolumn so sidebar state remains stable across refetches and reordering.'
  );

  const rowIndex = Number.parseInt(activeRow.rowId, 10);
  if (
    !Number.isNaN(rowIndex) &&
    rowIndex >= 0 &&
    rowIndex < items.length &&
    deepEqual(
      getDataProperties(items[rowIndex]),
      getDataProperties(activeRow.data)
    )
  ) {
    return rowIndex;
  }

  return items.findIndex((item) =>
    deepEqual(getDataProperties(item), getDataProperties(activeRow.data))
  );
}

export function isSameTableSidebarRow(
  left: TableSidebarRowData,
  right: TableSidebarRowData
): boolean {
  const leftBusinessId = getRowBusinessId(left.data);
  const rightBusinessId = getRowBusinessId(right.data);

  if (leftBusinessId != null && rightBusinessId != null) {
    return String(leftBusinessId) === String(rightBusinessId);
  }

  return deepEqual(getDataProperties(left.data), getDataProperties(right.data));
}

export function resolveActiveSidebarRowHighlightId(
  activeRow: TableSidebarRowData,
  items: TableRowItem[]
): string | undefined {
  const rowIndex = findActiveSidebarRowIndex(activeRow, items);

  return rowIndex >= 0 ? String(rowIndex) : undefined;
}

export function isActiveSidebarRowInTableItems(
  activeRow: TableSidebarRowData,
  items: TableRowItem[]
): boolean {
  return findActiveSidebarRowIndex(activeRow, items) >= 0;
}
